/**
 * Persistent notification toasts — used for VB6 transfer reminders
 * and excessive label reprint alerts.
 */
import React from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Printer, Package, X } from 'lucide-react';

interface Props {
  toastId: string;
  label: string;
  detail: string;
  icon?: React.ReactNode;
  /** Optional call-to-action rendered under the detail line. */
  action?: { label: string; onClick: () => void };
}

const PersistentToast: React.FC<Props> = ({ toastId, label, detail, icon, action }) => (
  <div className="flex items-start gap-3 max-w-sm">
    {icon || <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />}
    <div className="flex-1">
      <p className="font-semibold text-gray-900 text-sm">{label}</p>
      <p className="text-gray-600 text-sm mt-0.5">{detail}</p>
      {action && (
        <button
          onClick={() => { toast.dismiss(toastId); action.onClick(); }}
          className="mt-2 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
        >
          {action.label}
        </button>
      )}
    </div>
    <button
      onClick={() => toast.dismiss(toastId)}
      className="p-1 text-gray-400 hover:text-gray-600 rounded shrink-0"
      aria-label="dismiss"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
);

/** Show a persistent transfer reminder toast */
export function showTransferReminderToast(label: string, detail: string) {
  toast(
    (t) => <PersistentToast toastId={t.id} label={label} detail={detail} />,
    { duration: Infinity }
  );
}

/** Show a persistent reprint alert toast */
export function showReprintAlertToast(label: string, detail: string) {
  toast(
    (t) => <PersistentToast toastId={t.id} label={label} detail={detail} icon={<Printer className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />} />,
    { duration: Infinity }
  );
}

/**
 * Show a persistent packing material replenishment toast.
 *
 * `dedupeKey` should be the transfer number: auto-replenishment re-runs on every
 * sub-session start, so the same draft can be announced repeatedly. Keying by transfer
 * collapses those repeats into one toast while still letting a genuinely DIFFERENT
 * transfer raise its own — a constant key would silently replace one with the other.
 */
export function showPackingReplenishmentToast(
  label: string,
  detail: string,
  opts?: { action?: { label: string; onClick: () => void }; dedupeKey?: string }
) {
  toast(
    (t) => (
      <PersistentToast
        toastId={t.id}
        label={label}
        detail={detail}
        action={opts?.action}
        icon={<Package className="w-5 h-5 text-purple-500 shrink-0 mt-0.5" />}
      />
    ),
    {
      duration: Infinity,
      ...(opts?.dedupeKey ? { id: `packing-replenishment-${opts.dedupeKey}` } : {}),
    }
  );
}
